import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrgId } from '@/hooks/auth';
import { normalizePhoneForStorage } from '@/lib/phone-mask';
import { getPhoneVariants } from '@/lib/sonax/normalizePhone';

interface LookupCompany {
  id: string;
  name: string;
}

interface LookupLead {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  status: string | null;
  source: string | null;
  company_id: string | null;
  company?: LookupCompany | null;
}

interface LookupDeal {
  id: string;
  title: string | null;
  status: string | null;
  stage_id: string | null;
  amount: number | null;
  expected_close_date: string | null;
  lead_id: string | null;
  company_id: string | null;
  created_at: string;
}

type LookupKind = 'lead' | 'deal' | 'company' | 'none';

export interface PhoneLookupResult {
  kind: LookupKind;
  /** Lead encontrado (kind='lead' ou 'deal' com lead). */
  lead: LookupLead | null;
  /** Deal mais recente do lead (kind='deal' implica deal != null). */
  deal: LookupDeal | null;
  /** Company encontrada quando lead/deal não bateu (kind='company'). */
  company: LookupCompany | null;
  /** Telefone normalizado usado na busca (debug). */
  normalizedPhone: string;
}

const EMPTY_RESULT = (normalized: string): PhoneLookupResult => ({
  kind: 'none',
  lead: null,
  deal: null,
  company: null,
  normalizedPhone: normalized,
});

/**
 * Procura registros do CRM correspondentes a um telefone que está ligando.
 *
 * Estratégia (na ordem):
 *   1. Lead com `phone` matching (qualquer status, mais recente primeiro).
 *   2. Se achou lead: busca Deal mais recente desse lead (qualquer status).
 *   3. Se NÃO achou lead: tenta Company com `phone` matching.
 *
 * O telefone é normalizado pra `55DDDNUMERO` antes da busca, e também
 * tentamos a forma sem `55` (10/11 dígitos) — pra cobrir bases antigas que
 * gravaram sem código de país.
 */
export function useLookupByPhone(rawPhone: string | null | undefined) {
  const { orgId } = useCurrentOrgId();

  return useQuery({
    queryKey: ['sonax-incoming-lookup', orgId, rawPhone],
    queryFn: async (): Promise<PhoneLookupResult> => {
      if (!rawPhone || !orgId) return EMPTY_RESULT(rawPhone ?? '');

      const normalized = normalizePhoneForStorage(rawPhone);
      if (!normalized) return EMPTY_RESULT(rawPhone);

      // Variantes pra tentar. `getPhoneVariants` cobre a base anterior ao backfill de
      // 20260410200000, onde móvel antigo ficou gravado sem o 9º dígito; a cada variante
      // acrescentamos também a forma sem o DDI 55, para bases mais antigas ainda.
      const variants = new Set<string>();
      for (const variant of getPhoneVariants(normalized)) {
        variants.add(variant);
        if (variant.startsWith('55') && variant.length >= 12) {
          variants.add(variant.slice(2));
        }
      }

      // 1) Procura LEAD por qualquer variante
      const { data: leads, error: leadErr } = await supabase
        .from('leads')
        .select('id, name, phone, email, status, source, company_id, company:companies(id, name)')
        .eq('org_id', orgId)
        .in('phone', Array.from(variants))
        .order('created_at', { ascending: false })
        .limit(1);

      if (leadErr) {
        console.warn('[useLookupByPhone] leads query error:', leadErr);
      }

      let lead = (leads ?? [])[0] as unknown as LookupLead | undefined;

      // `leads.phone` guarda só o PRINCIPAL. Uma ligação que chega num número secundário não
      // casa ali — e é justamente o caso que o multi-telefone cria.
      if (!lead) {
        const { data: matches } = await supabase
          .from('lead_phones')
          .select('lead_id')
          .eq('org_id', orgId)
          .in('phone', Array.from(variants))
          .limit(5);

        const leadId = (matches ?? [])[0]?.lead_id;
        if (leadId) {
          const { data: byPhoneTable } = await supabase
            .from('leads')
            .select('id, name, phone, email, status, source, company_id, company:companies(id, name)')
            .eq('id', leadId)
            .limit(1);
          lead = (byPhoneTable ?? [])[0] as unknown as LookupLead | undefined;
        }
      }

      if (lead) {
        // 2) Lead encontrado — busca Deal mais recente desse lead
        const { data: deals } = await supabase
          .from('deals')
          .select('id, title, status, stage_id, amount, expected_close_date, lead_id, company_id, created_at')
          .eq('org_id', orgId)
          .eq('lead_id', lead.id)
          .order('created_at', { ascending: false })
          .limit(1);

        const deal = (deals ?? [])[0] as unknown as LookupDeal | undefined;

        return {
          kind: deal ? 'deal' : 'lead',
          lead,
          deal: deal ?? null,
          company: lead.company ?? null,
          normalizedPhone: normalized,
        };
      }

      // 3) Sem lead — tenta company
      const { data: companies } = await supabase
        .from('companies')
        .select('id, name')
        .eq('org_id', orgId)
        .in('phone', Array.from(variants))
        .order('created_at', { ascending: false })
        .limit(1);

      const company = (companies ?? [])[0] as unknown as LookupCompany | undefined;
      if (company) {
        return {
          kind: 'company',
          lead: null,
          deal: null,
          company,
          normalizedPhone: normalized,
        };
      }

      return EMPTY_RESULT(normalized);
    },
    enabled: !!rawPhone && !!orgId,
    staleTime: 60_000,
    retry: false,
  });
}
